import React from "react";
import { KgCollapsedNodeHover } from "../KgCollapsedNodeHover";
import { rawColors } from "../../../design-system/tokens/colors";
import { Card, IconBox, Label, Name, TextStack } from "./KgArtifactNodeCard.styles";
import type { KgArtifactKind, KgArtifactNodeCardProps } from "./KgArtifactNodeCard.types";

/** Monitor / presentation screen — for PPT, Doc etc. */
const MonitorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M5.33 13.33h5.34" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 11.33v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

/** Play / video icon. */
const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7 6.5l3 1.5-3 1.5v-3z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round"/>
  </svg>
);

/** Memo / notes icon. */
const MemoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="2" width="10" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M5.33 5.33h5.34M5.33 8h5.34M5.33 10.67h3.34" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const KIND_ICON: Record<KgArtifactKind, React.ReactNode> = {
  PPT: <MonitorIcon />,
  Doc: <MonitorIcon />,
  PDF: <MonitorIcon />,
  Video: <PlayIcon />,
  Memo: <MemoIcon />,
};

const resolveIcon = (kind: string | undefined, override: React.ReactNode | undefined): React.ReactNode => {
  if (override) return override;
  if (kind && kind in KIND_ICON) return KIND_ICON[kind as KgArtifactKind];
  return <MonitorIcon />;
};

export const KgArtifactNodeCard: React.FC<KgArtifactNodeCardProps> = ({
  name,
  kind = "Output",
  icon,
  collapsed = false,
  visualState = "default",
  isSelected = false,
  onClick,
}) => {
  const renderedIcon = resolveIcon(kind, icon);

  if (collapsed) {
    return (
      <KgCollapsedNodeHover
        icon={renderedIcon}
        color={rawColors.accent.artifactBlue}
        bgColor={rawColors.accent.artifactBlueBg}
        visualState={visualState}
        title={name}
        subtitle={`Output · ${kind}`}
      />
    );
  }

  return (
    <Card $visualState={visualState} $isSelected={isSelected} onClick={onClick} role="group" tabIndex={0}>
      <IconBox>{renderedIcon}</IconBox>
      <TextStack>
        <Label title={`Output · ${kind}`}>Output · {kind}</Label>
        <Name title={name}>{name}</Name>
      </TextStack>
    </Card>
  );
};

export default KgArtifactNodeCard;
